import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, forwardRef } from "react";
import {
  Viewicon,
  DeleteConvgIcon,
  EditConvgIcon,
} from "../../../../Components/IconComponents";
import Table from "../../../../Components/UiComponents/Table/Table";
import { useTranslation } from "react-i18next";
import {
  contactTableData,
  contactcolumnsForExport,
} from "../../../../JsonData/ContactConfig";
import {
  deleteContactURL,
  getAllContactsByRole,
  getThemeURL,
} from "../../../../Services/EndPoints";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../../Services/Services";
import { getExportedColumnsData } from "../../../../Components/UiComponents/utils";
import { setTableData } from "../../../../CentralStore/ExportLMSTable/dataSlice";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import Loader from "react-spinner-loader";
import { RoleContext } from "../../../..";

// Main component for displaying and managing contact data in a table format
const ContactTable = forwardRef(() => {
  const { roleName } = useContext(RoleContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [contactData, setContactData] = useState(contactTableData);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [searchData, setSearchData] = useState([]);
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
  });
  let contactRegionMap = {};

  // Fetch user profile data on component mount
  useEffect(() => {
    getUserProfile();
  }, []);

  // Send search data to central store whenever searchData changes
  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);

  // Navigate to create new contact page
  const addNewHandler = () => {
    navigate("/contact-management/create-contact");
  };

  const parseUserPermissions = (webPermissions) => {
    if (!webPermissions) {
      return { canView: false, canEdit: false, canDelete: false };
    }
    const permissions = webPermissions.split(",").map(p => p.trim().toUpperCase());
    return {
      canView: permissions.includes("V"),
      canEdit: permissions.includes("E"),
      canDelete: permissions.includes("D"),
    };
  };


  // Remove duplicate contacts and build region mapping
  const getSanitizedContactList = (contactList) => {
    let contactListUnique = [];
    let contactListTempMap = [];

    contactList.forEach(function (contact) {
      if (
        !(
          contactListTempMap.indexOf(contact["type"] + "_" + contact["id"]) > -1
        )
      ) {
        contactListUnique.push(contact);
      }
      contactListTempMap.push(contact["type"] + "_" + contact["id"]);
      if (contact["region_name"]) {
        if (contactRegionMap[contact["type"] + "_" + contact["id"]]) {
          let regionArr =
            contactRegionMap[contact["type"] + "_" + contact["id"]];
          regionArr.push(contact["region_name"]);
        } else {
          contactRegionMap[contact["type"] + "_" + contact["id"]] = [
            contact["region_name"],
          ];
        }
      }
    });
    return contactListUnique;
  };

  // Fetch user profile and initiate contact list retrieval
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        const permissions = parseUserPermissions(userProfile[0].web_permissions);
        setUserPermissions(permissions);
        getContactList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Create API payload based on user profile role and bank information
  const createPayload = (profile) => {
    let admin_role = "U"; // default value

    if (profile.role_name === "HO") {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  // Fetch and process contact list based on user profile
  const getContactList = async (profile) => {
    setLoader(true);
    const payload = createPayload(profile);
    var contactList = await postDataWithBody(getAllContactsByRole, payload, {});

    contactList = getSanitizedContactList(contactList);

    setLoader(false);
    setContactData(contactList);
    setRowCount(contactList.length);
  };

  // Dispatch table data to Redux store for export functionality
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = contactcolumnsForExport;
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, data),
        })
      );
    }
  };

  // Define table columns configuration with sorting and custom cell rendering
  const columns = React.useMemo(
    () => [
      {
        Header: t("contacttableHeaders.name"),
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: t("Bank Name"),
        accessor: "bank_name",
        disableSortBy: true,
        Cell: ({ value }) => (value ? value : "NA"),
      },
      {
        Header: t("Branch Name"),
        accessor: "branch_name",
        disableSortBy: true,
        Cell: ({ value }) => (value ? value : "NA"),
      },
      {
        Header: t("contacttableHeaders.jobtitle"),
        accessor: "job_title",
        disableSortBy: true,
      },

      {
        Header: t("contacttableHeaders.phone1"),
        accessor: "phone1",
        disableSortBy: true,
      },
      {
        Header: t("contacttableHeaders.email"),
        accessor: "email",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            let emailText = props.value;
            if (props.value.length > 20) {
              emailText = props.value.substring(0, 20) + "...";
            }
            return <span title={props.value}>{emailText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("contacttableHeaders.agencyname"),
        accessor: "agency_name",
        Cell: (props) => {
          if (props.value) {
            let text = props.value;
            if (props.value.length > 15) {
              text = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{text}</span>;
          } else {
            return "NA";
          }
        },

        disableSortBy: true,
      },
      ...(userPermissions.canView || userPermissions.canEdit || userPermissions.canDelete
      ? [{
          Header: t("contacttableHeaders.action"),
          disableSortBy: true,
          disableFilters: true,
          Cell: (props) => {
            const [isdelete, setIsdelete] = useState(false);

            const deletePopUp = () => setIsdelete(!isdelete);

            const deleteTableDataById = async (id) => {
              let data = { id: id };
              var res = await putDataWithBody(deleteContactURL, data, {});
              if (res.status === "success") {
                getUserProfile(); // reload data
              }
              toast.success(res.msg, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
              });
              setIsdelete(false);
            };

            return (
              <div className="usertable__actionsicon">
                {/* View button */}
                {userPermissions.canView && (
                  <span
                    onClick={() =>
                      navigate("/contact-management/view-contact-details", {
                        state: { data: props.row.original },
                      })
                    }
                  >
                    <Viewicon className="cursor-pointer me-2" />
                  </span>
                )}

                {/* Edit button */}
                {userPermissions.canEdit && (
                  <span
                    onClick={() =>
                      navigate("/contact-management/create-contact", {
                        state: { data: props.row.original, edit: true },
                      })
                    }
                  >
                    <EditConvgIcon className="cursor-pointer me-2" />
                  </span>
                )}

                {/* Delete button */}
                {userPermissions.canDelete && (
                  <span onClick={deletePopUp}>
                    <DeleteConvgIcon className="cursor-pointer mr-1" />
                  </span>
                )}

                {/* Delete confirmation popup */}
                {isdelete && (
                  <Confirmpopup
                    className="DeltePopup"
                    show={isdelete}
                    title={t("deletepopuptext")}
                    message={t("deletepopupmessage")}
                    handleToggle={deletePopUp}
                    row={props.row}
                    name={props.row.original.name}
                    deleteRow={deleteTableDataById}
                    cancel={t("cancelbutton")}
                    action={t("actionbutton")}
                  />
                )}
              </div>
            );
          },
        }]
      : []),
    ],
    [t, roleName, userPermissions]
  );
  return (
    <div className="create-user">
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div className="datatable__container">
        {contactTableData && (
          <Table
            data={contactData}
            columns={columns}
            numberOfRows={10}
            setSearchData={setSearchData}
            addButtonLabel={t("contactaddButtonLabel")}
            addNewHandler={addNewHandler}
            showExport={false}
            showFilter={false}
            showReset={false}
            onExpand={false}
            rowCount={rowCount} // Pass rowCount to the Table component
            enableColumnFilters={true}
            headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid  #f0f0f0",
              borderRadius: "4px",
            }}
          />
        )}
      </div>
    </div>
  );
});

export default ContactTable;
